import { useEffect, useState } from 'preact/hooks';
import { useModel, type Settings as S } from '../model.ts';
import { State, studyDayStart } from '../srs.ts';
import { wipeDatabase } from '../db.ts';
import { CANTONESE_TONES, Reading } from '../Reading.tsx';
import type { Syncer } from '../sync.ts';

function ago(t: number | null): string {
  if (!t) return 'never';
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(t).toLocaleDateString();
}

export function Settings({ syncer }: { syncer: Syncer }) {
  const m = useModel();
  const s = m.settings;
  const set = (patch: Partial<S>) => void m.saveSettings(patch);

  const live = new Set(m.liveNotes().map((n) => n.id));
  const cards = [...m.cards.values()].filter((c) => {
    const n = m.notes.get(c.noteId);
    return live.has(c.noteId) && !!n && !!m.deckOf(n)?.kinds[c.kind];
  });
  const learned = cards.filter((c) => c.fsrs.state === State.Review).length;
  const dayStart = studyDayStart(Date.now(), s.dayStartHour);
  const tomorrowEnd = dayStart + 2 * 86_400_000;
  const dueTomorrow = cards.filter(
    (c) => c.fsrs.state === State.Review && c.fsrs.dueAt.getTime() >= dayStart + 86_400_000 && c.fsrs.dueAt.getTime() < tomorrowEnd,
  ).length;
  let answers = 0;
  let today = 0;
  for (const list of m.reviewsByCard.values()) {
    for (const r of list) {
      if (r.deleted) continue;
      answers++;
      if (r.reviewedAt >= dayStart) today++;
    }
  }

  async function signOut() {
    // Revoke this device's token on the server too, if we can reach it; offline, just forget it here.
    await fetch('/api/devices/self', { method: 'DELETE', headers: { Authorization: `Bearer ${m.token}` } }).catch(
      () => {},
    );
    await m.setToken(null);
    location.reload();
  }

  async function forget() {
    const warn = m.sync.pending
      ? `${m.sync.pending} change(s) on this device haven't synced and will be lost. `
      : '';
    if (!confirm(`${warn}Remove all data from this device? Everything synced stays on the server.`)) return;
    await wipeDatabase(m.db);
    location.reload();
  }

  return (
    <div class="settings">
      <section>
        <h3>Sync</h3>
        <p>
          {m.sync.status === 'syncing'
            ? 'Syncing…'
            : m.sync.status === 'offline'
              ? 'Offline. Changes are saved here and sync when you are back online.'
              : m.sync.status === 'error'
                ? `Last sync failed: ${m.sync.error}`
                : m.sync.status === 'signed-out'
                  ? (m.sync.error ?? 'Not signed in.')
                  : `Synced ${ago(m.sync.lastSyncAt)}.`}
          {m.sync.pending > 0 && ` ${m.sync.pending} change(s) waiting to upload.`}
        </p>
        <button onClick={() => void syncer.run()} disabled={m.sync.status === 'syncing'}>
          Sync now
        </button>
      </section>

      <section>
        <h3>Today</h3>
        <ul class="stats">
          <li>
            <b>{today}</b> answers today
          </li>
          <li>
            <b>{learned}</b> of {cards.length} cards learned
          </li>
          <li>
            <b>{dueTomorrow}</b> due tomorrow
          </li>
          <li>
            <b>{answers}</b> answers all time
          </li>
        </ul>
      </section>

      <section>
        <h3>Study</h3>
        <label class="row">
          Maximum reviews per day
          <input
            type="number"
            min={0}
            max={9999}
            value={s.maxReviews}
            onChange={(e) => set({ maxReviews: Math.max(0, Number(e.currentTarget.value) || 0) })}
          />
        </label>
        <label class="row">
          Play audio automatically
          <input type="checkbox" checked={s.autoplay} onChange={(e) => set({ autoplay: e.currentTarget.checked })} />
        </label>
        <label class="row">
          <span>
            Show written text <span class="muted small">on audio decks' answers (characters, for Cantonese)</span>
          </span>
          <input type="checkbox" checked={s.showText} onChange={(e) => set({ showText: e.currentTarget.checked })} />
        </label>
        <label class="row">
          Slow playback speed
          <select value={String(s.slowRate)} onChange={(e) => set({ slowRate: Number(e.currentTarget.value) })}>
            {[0.5, 0.6, 0.7, 0.8, 0.9].map((r) => (
              <option key={r} value={String(r)}>
                {r}×
              </option>
            ))}
          </select>
        </label>
        <p class="muted small">
          These settings are per device. Decks, cards and progress sync; new cards per day and card types are set per
          deck under Decks.
        </p>
      </section>

      {m.liveDecks().some((d) => d.language === 'yue') && (
      <section>
        <h3>Cantonese tones</h3>
        <ul class="tones">
          {CANTONESE_TONES.map((t) => (
            <li key={t.n}>
              <Reading text={`si${t.n}`} tones /> {t.name} <span class="muted">({t.contour})</span>
            </li>
          ))}
        </ul>
        <p class="muted small">
          Syllables are coloured by tone. si1 詩 poem, si2 史 history, si3 試 try, si4 時 time, si5 市 market, si6 事
          matter.
        </p>
      </section>
      )}

      <Devices token={m.token} />

      <section>
        <h3>This device</h3>
        <p class="muted small">
          On iPhone, use Share → Add to Home Screen; on Android, Install app from the browser menu. It works offline
          once installed.
        </p>
        <div class="form-actions">
          <button onClick={signOut}>Sign out</button>
          <button class="danger" onClick={forget}>
            Remove data from this device
          </button>
        </div>
      </section>
    </div>
  );
}

interface DeviceRow {
  id: string;
  email: string;
  label: string;
  createdAt: number;
  lastSeenAt: number;
}

/** Devices signed in through Pocket ID, each with its own revocable token. Needs the network. */
function Devices({ token }: { token: string | null }) {
  const [data, setData] = useState<{ current: string | null; devices: DeviceRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const res = await fetch('/api/devices', { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`server said ${res.status}`);
      setData(await res.json());
      setError(null);
    } catch (e) {
      setError(e instanceof TypeError ? 'Offline: the device list needs the server.' : String((e as Error).message));
    }
  }

  useEffect(() => void load(), [token]);

  async function revoke(d: DeviceRow) {
    if (!confirm(`Sign out ${d.label}? It keeps its local data but can't sync until it signs in again.`)) return;
    await fetch(`/api/devices/${d.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    await load();
  }

  return (
    <section>
      <h3>Signed-in devices</h3>
      {error && <p class="muted small">{error}</p>}
      {data && !data.devices.length && (
        <p class="muted small">None yet. Devices that sign in with Pocket ID show up here.</p>
      )}
      {data && data.devices.length > 0 && (
        <ul class="stats">
          {data.devices.map((d) => (
            <li key={d.id} class="device">
              <span>
                <b>{d.label}</b>
                {d.id === data.current && <span class="badge">this device</span>}
                <br />
                <span class="muted small">
                  {d.email} · last seen {ago(d.lastSeenAt)}
                </span>
              </span>
              {d.id !== data.current && (
                <button class="link" onClick={() => void revoke(d)}>
                  Sign out
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
